import "@/index.css"; // need to be imported before primevue
import { type Locale } from "shared-core/services/i18n.ts";
import { loadLocaleNamespaceFromVite } from "shared-ui/services/i18n-locale-loader.ts";
import { createI18nRuntime } from "shared-ui/services/i18n-runtime.ts";
import { I18nRuntimeKey } from "shared-ui/services/vue-i18n.ts";
import { vueErrorHandler } from "shared-ui/utils/vue-error-handler.ts";
import { ViteSSG } from "vite-ssg";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import { routes } from "vue-router/auto-routes";
import App from "@/App.vue";
import { posts } from "@/services/blog.ts";
import { getStaticRoutes } from "@/utils/static-routes.ts";

// Vite processes the blog metadata and its image imports in the server entry.
export function includedRoutes(paths: string[]): string[] {
  return getStaticRoutes(paths, posts);
}

function localeFromRoute(to: RouteLocationNormalizedLoaded): Locale {
  const p = (to.params as Record<string, unknown>)["locale"];
  return p === "sv" ? "sv" : "en";
}

export const createApp = ViteSSG(
  App,
  {
    routes,
    scrollBehavior(to, _from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.hash) {
        return { el: to.hash, top: 0 };
      }
      return { top: 0 };
    },
  },
  async ({ app, router }) => {
    const [
      { default: PrimeVue },
      { default: ConfirmationService },
      { default: ToastService },
      { default: Tooltip },
    ] = await Promise.all([
      import("primevue/config"),
      import("primevue/confirmationservice"),
      import("primevue/toastservice"),
      import("primevue/tooltip"),
    ]);

    app.use(PrimeVue, {
      theme: "none",
    });

    const i18nRuntime = createI18nRuntime(loadLocaleNamespaceFromVite);
    const { i18n, setLocale } = i18nRuntime;
    app.use(i18n);
    app.provide(I18nRuntimeKey, i18nRuntime);

    // 1) Set locale for the very first render of this route
    await setLocale(localeFromRoute(router.currentRoute.value));

    // 2) Keep it in sync for subsequent navigations (client only)
    router.beforeResolve(async (to) => {
      const next = localeFromRoute(to);
      if (i18n.global.locale.value !== next) {
        await setLocale(next);
      }
      return true;
    });

    app.use(ToastService);
    app.use(ConfirmationService);

    app.directive("tooltip", Tooltip);

    app.config.errorHandler = (
      err: unknown,
      instance: unknown,
      info: string,
    ): void => {
      vueErrorHandler(
        err,
        instance,
        info,
        console.error,
        router.currentRoute.value,
      );
    };
  },
);
